export const identifyComponent = (element: Element | null): string | undefined => {
    if (!element) return undefined;

    let current = element;
    const depth = 5; // Search up 5 levels

    for (let i = 0; i < depth; i++) {
        if (!current) break;

        // 1. Check for data-component attribute (common pattern)
        if (current.getAttribute('data-component')) {
            return current.getAttribute('data-component') || undefined;
        }
        
        // 2. Check for data-testid
        if (current.getAttribute('data-testid')) {
            return current.getAttribute('data-testid') || undefined;
        }

        // 3. React DevTools internals (brittle, but sometimes works)
        // @ts-ignore
        const key = Object.keys(current).find(k => k.startsWith('__reactFiber$'));
        if (key) {
            // @ts-ignore
            const fiber = current[key];
            if (fiber?.type?.displayName) {
                return fiber.type.displayName;
            }
            if (fiber?.type?.name) {
                return fiber.type.name;
            }
        }

        // 4. ID or specific classes
        if (current.id) return `#${current.id}`;

        if (current.parentElement) {
            current = current.parentElement;
        } else {
            break;
        }
    }
    
    return undefined;
};

export const getElementInfo = (element: Element | null): string | undefined => {
    if (!element) return undefined;
    
    const tagName = element.tagName.toLowerCase();
    const classes = element.className && typeof element.className === 'string' 
        ? element.className.split(' ').filter(c => c.trim()).slice(0, 3).join(' ')
        : '';
    
    let info = `<${tagName}`;
    if (element.id) info += ` id="${element.id}"`;
    if (classes) info += ` class="${classes}"`;
    info += '>';
    
    return info;
};
/**
 * Ids that aren't valid CSS identifiers — React's `useId` (`:r1:`, `«r1»`), ids
 * starting with a digit — made an unparseable selector, so the pin lost its
 * element and fell back to page coordinates.
 */
const escapeId = (id: string): string =>
    typeof CSS !== 'undefined' && typeof CSS.escape === 'function' ? CSS.escape(id) : id;

/**
 * Generates a stable CSS selector for a DOM element, preferring IDs and
 * falling back to a tag + nth-of-type path anchored to the nearest ID ancestor.
 */
export const getElementSelector = (element: Element): string => {
    // If the element itself has an id, that's unique enough.
    if (element.id) {
        return `#${escapeId(element.id)}`;
    }

    const parts: string[] = [];
    let el: Element | null = element;
    // The element's own document — it may live in the device preview frame.
    const doc = element.ownerDocument;

    while (el && el !== doc.documentElement && el !== doc.body) {
        let part = el.tagName.toLowerCase();

        if (el.id) {
            // Anchor the path here – an id is unique.
            parts.unshift(`#${escapeId(el.id)}`);
            break;
        }

        // Add nth-of-type disambiguator if there are siblings of the same tag.
        const parent = el.parentElement;
        if (parent) {
            const sameTagSiblings = Array.from(parent.children).filter(
                (c) => c.tagName === el!.tagName,
            );
            if (sameTagSiblings.length > 1) {
                const index = sameTagSiblings.indexOf(el) + 1;
                part += `:nth-of-type(${index})`;
            }
        }

        parts.unshift(part);
        el = el.parentElement;
    }

    return parts.join(' > ') || element.tagName.toLowerCase();
};